import React, { useState } from "react";
import { orderBy } from "lodash";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Deliveries } from "./types";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import { getFileNameFromPath } from "@/helpers/filename";
import { formatDateTimeForDelivery } from "@/helpers/date";
import CompletedIcon from "@/assets/CompletedIcon";
import { Theme, useTheme } from "@/contexts/ThemeContext";
import { CircleAlert, Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface DeliveryCheckIconProps {
  deliveries: Deliveries[];
  taskShotId: string;
}

const DeliveryCheckIcon: React.FC<DeliveryCheckIconProps> = ({
  deliveries,
  taskShotId,
}) => {
  const { theme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const delivery = orderBy(deliveries, (v) => v?.created, "desc")[0];

  if (!delivery) {
    return null;
  }

  const deliveredFiles = delivery.deliveryFilesToSourceTaskShotIds.filter(
    (item) => item.taskShotId === taskShotId,
  );

  const infectedFiles = delivery.infectedFilesToSourceTaskShotIds.filter(
    (item) => item.taskShotId === taskShotId,
  );
  const metadataMismatches = delivery.metadataMismatches?.find(
    (mm) => mm.taskShotId === taskShotId,
  )?.mismatches;

  const hasPendingPreview = metadataMismatches?.some(
    (mismatch) => mismatch.checkType === "Pending Preview",
  );

  const actualMismatches = metadataMismatches?.filter(
    (mismatch) => mismatch.checkType !== "Pending Preview",
  );

  if (infectedFiles.length) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <CircleAlert className="text-[#FF0000] h-4 w-4" />
          </TooltipTrigger>
          <TooltipContent>
            <div>
              <div>
                <LongContentWrapper>
                  {infectedFiles[0].error}
                </LongContentWrapper>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (deliveredFiles.some((file) => file.isBroken)) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <CircleAlert className="text-[#FF0000] h-4 w-4" />
          </TooltipTrigger>
          <TooltipContent>
            <div>
              <div>
                <LongContentWrapper>
                  Download link broken. please re-deliver
                </LongContentWrapper>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // If only pending preview, show grey icon
  if (
    hasPendingPreview &&
    (!actualMismatches || actualMismatches.length === 0)
  ) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mt-[1px]">
              <CompletedIcon
                color={theme == Theme.Light ? "#9ca3af" : "#6b7280"}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="!p-3 !bg-primary border border-accent-foreground flex flex-col gap-2"
          >
            <div className="text-gray-500 dark:text-gray-400">
              Metadata check pending
            </div>
            {deliveredFiles.map((file) => (
              <div key={file.key} className="truncate font-medium">
                {getFileNameFromPath(file.key)}
              </div>
            ))}
            <span>
              Delivered on &nbsp;
              {formatDateTimeForDelivery(
                delivery.created,
                delivery.creator.timezone,
              )}{" "}
              ({delivery.creator.timezoneName}) by &nbsp;
              {delivery.vendor.profile.companyName}
            </span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // If there are actual metadata mismatches, show orange icon
  if (actualMismatches && actualMismatches.length > 0) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <button className="flex items-center cursor-pointer">
                    <CompletedIcon color="#f97316" className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
              </DialogTrigger>
              <DialogContent className="max-w-3xl sm:max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>Metadata Mismatch Details</DialogTitle>
                    <button
                      onClick={async () => {
                        try {
                          // Build both TSV and HTML content
                          let tsvContent = "Key\tSource\tDelivery\n";
                          let htmlContent =
                            '<table border="1" style="border-collapse: collapse; width: 100%;"><thead><tr><th style="padding: 8px; text-align: left; background-color: #f3f4f6;">Key</th><th style="padding: 8px; text-align: left; background-color: #f3f4f6;">Source</th><th style="padding: 8px; text-align: left; background-color: #f3f4f6;">Delivery</th></tr></thead><tbody>';

                          actualMismatches?.forEach((mismatch) => {
                            const allKeys = new Set<string>();
                            mismatch.sourceItems?.forEach((item) =>
                              allKeys.add(item.key),
                            );
                            mismatch.deliveryItems?.forEach((item) =>
                              allKeys.add(item.key),
                            );

                            const sortedKeys = Array.from(allKeys)
                              .map((key) => {
                                const sourceItem = mismatch.sourceItems?.find(
                                  (item) => item.key === key,
                                );
                                const deliveryItem =
                                  mismatch.deliveryItems?.find(
                                    (item) => item.key === key,
                                  );
                                const valuesDiffer =
                                  sourceItem?.value !== deliveryItem?.value;
                                return {
                                  key,
                                  sourceItem,
                                  deliveryItem,
                                  valuesDiffer,
                                };
                              })
                              .sort((a, b) =>
                                a.valuesDiffer === b.valuesDiffer
                                  ? 0
                                  : a.valuesDiffer
                                    ? -1
                                    : 1,
                              );

                            tsvContent += `\n${mismatch.checkType}\n`;
                            htmlContent += `<tr><td colspan="3" style="padding: 8px; font-weight: bold; background-color: #e5e7eb;">${mismatch.checkType}</td></tr>`;

                            sortedKeys.forEach(
                              ({
                                key,
                                sourceItem,
                                deliveryItem,
                                valuesDiffer,
                              }) => {
                                const sourceValue = sourceItem
                                  ? sourceItem.value
                                  : "-";
                                const deliveryValue = deliveryItem
                                  ? deliveryItem.value
                                  : "-";

                                // TSV
                                tsvContent += `${key}\t${sourceValue}\t${deliveryValue}\n`;

                                // HTML
                                const keyStyle = valuesDiffer
                                  ? "color: #dc2626; font-weight: 600;"
                                  : "";
                                htmlContent += `<tr><td style="padding: 8px; ${keyStyle}">${key}</td><td style="padding: 8px;">${sourceValue}</td><td style="padding: 8px;">${deliveryValue}</td></tr>`;
                              },
                            );
                          });

                          htmlContent += "</tbody></table>";

                          // Write both formats to clipboard
                          const htmlBlob = new Blob([htmlContent], {
                            type: "text/html",
                          });
                          const textBlob = new Blob([tsvContent], {
                            type: "text/plain",
                          });

                          await navigator.clipboard.write([
                            new ClipboardItem({
                              "text/html": htmlBlob,
                              "text/plain": textBlob,
                            }),
                          ]);

                          setCopied(true);
                          toast.success("Copied to clipboard");
                          setTimeout(() => setCopied(false), 2000);
                        } catch (error) {
                          toast.error("Failed to copy");
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 text-green-600" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    The delivered files have metadata that doesn't match the
                    source files:
                  </p>

                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm table-fixed">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium w-1/2">
                            Key
                          </th>
                          <th className="px-4 py-2 text-left font-medium w-1/4">
                            Source
                          </th>
                          <th className="px-4 py-2 text-left font-medium w-1/4">
                            Delivery
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {actualMismatches.map((mismatch, checkIndex) => (
                          <React.Fragment key={checkIndex}>
                            <tr className="bg-gray-100 dark:bg-gray-700">
                              <td
                                className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200"
                                colSpan={3}
                              >
                                {mismatch.checkType}
                              </td>
                            </tr>

                            {(() => {
                              const allKeys = new Set<string>();
                              mismatch.sourceItems?.forEach((item) =>
                                allKeys.add(item.key),
                              );
                              mismatch.deliveryItems?.forEach((item) =>
                                allKeys.add(item.key),
                              );

                              const sortedKeys = Array.from(allKeys)
                                .map((key) => {
                                  const sourceItem = mismatch.sourceItems?.find(
                                    (item) => item.key === key,
                                  );
                                  const deliveryItem =
                                    mismatch.deliveryItems?.find(
                                      (item) => item.key === key,
                                    );
                                  const valuesDiffer =
                                    sourceItem?.value !== deliveryItem?.value;
                                  return {
                                    key,
                                    sourceItem,
                                    deliveryItem,
                                    valuesDiffer,
                                  };
                                })
                                .sort((a, b) =>
                                  a.valuesDiffer === b.valuesDiffer
                                    ? 0
                                    : a.valuesDiffer
                                      ? -1
                                      : 1,
                                );

                              return sortedKeys.map(
                                ({
                                  key,
                                  sourceItem,
                                  deliveryItem,
                                  valuesDiffer,
                                }) => {
                                  return (
                                    <tr
                                      key={`${checkIndex}-${key}`}
                                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                    >
                                      <td className="px-4 py-2">
                                        <div
                                          className={`font-mono text-xs font-medium break-words ${
                                            valuesDiffer
                                              ? "text-red-600 dark:text-red-400"
                                              : ""
                                          }`}
                                        >
                                          {key}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2">
                                        {sourceItem ? (
                                          <div className="font-mono text-xs break-words">
                                            {sourceItem.value}
                                          </div>
                                        ) : (
                                          <div className="text-gray-400 dark:text-gray-500 italic text-xs">
                                            -
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-4 py-2">
                                        {deliveryItem ? (
                                          <div className="font-mono text-xs break-words">
                                            {deliveryItem.value}
                                          </div>
                                        ) : (
                                          <div className="text-gray-400 dark:text-gray-500 italic text-xs">
                                            -
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                },
                              );
                            })()}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="pt-2 border-t">
                    <p className="text-sm">
                      <strong>Delivered on:</strong>{" "}
                      {formatDateTimeForDelivery(
                        delivery.created,
                        delivery.creator.timezone,
                      )}{" "}
                      ({delivery.creator.timezoneName}) by{" "}
                      {delivery.vendor.profile.companyName}
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <TooltipContent
              side="bottom"
              className="!p-3 !bg-primary border border-accent-foreground flex flex-col gap-2"
            >
              <div className="text-orange-600 dark:text-orange-400 font-medium">
                Metadata mismatch
              </div>
              {deliveredFiles.map((file) => (
                <div key={file.key} className="truncate font-medium">
                  {getFileNameFromPath(file.key)}
                </div>
              ))}
              <span>
                Delivered on &nbsp;
                {formatDateTimeForDelivery(
                  delivery.created,
                  delivery.creator.timezone,
                )}{" "}
                ({delivery.creator.timezoneName}) by &nbsp;
                {delivery.vendor.profile.companyName}
              </span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="mt-[1px]">
            <CompletedIcon
              color={theme == Theme.Light ? "#007826" : "#00ba00"}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="!p-3 !bg-primary border border-accent-foreground flex flex-col gap-2"
        >
          {deliveredFiles.map((file) => (
            <div key={file.key} className="truncate font-medium">
              {getFileNameFromPath(file.key)}
            </div>
          ))}
          <span>
            Delivered on &nbsp;
            {formatDateTimeForDelivery(
              delivery.created,
              delivery.creator.timezone,
            )}{" "}
            ({delivery.creator.timezoneName}) by &nbsp;
            {delivery.vendor.profile.companyName}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DeliveryCheckIcon;
